import { Form, Popup } from 'devextreme-react';
import { RequiredRule } from 'devextreme-react/data-grid';
import { ButtonItem, ButtonOptions, GroupItem, Label, SimpleItem } from 'devextreme-react/form';
import React, { useEffect, useRef, useState } from 'react';
import useSWR from 'swr';
import '../../../assets/bulk-modal.scss';
import { useAssetClass } from '../../../contexts/assetClass';
import { useLoading } from '../../../contexts/loadingContext';
import { useTranslation } from '../../../contexts/translation';
import { swrConfig } from '../../../pages/Assets/AssetsEdit';
import { CapacityCategory } from '../../../types/capacityCategory.types';
import { DataGridActionType } from '../../../types/dataGridActionTypes';
import { Step } from '../../../types/step.types';
import { Task } from '../../../types/task.types';
import { API_URL, fetcher } from '../../../utils/apiUrl';
import '../EditCreateModal.scss';
import '../TasksBulkEditModal.scss';

type Props = {
  data: any;
  isVisible: boolean;
  mutate?: () => void;
  onClose: () => void;
  onSubmit: (data: any, action: 'customStep' | 'step' | 'stepGroup') => void;
  actionType: DataGridActionType;
  stepList?: Step[];
  transFormName: string;
  popupWidth: string;
  taskId: number;
};

/**
 * Modalová komponent pro vytváření/editaci záznamů typu Task step
 */
export default function TaskStepModal({
  data,
  isVisible,
  actionType,
  onClose,
  onSubmit,
  stepList,
  transFormName,
  popupWidth,
  taskId,
}: Props) {
  const { translate } = useTranslation();
  const { assetClass } = useAssetClass();
  const formRef = useRef<Form>(null);
  const { loading } = useLoading();
  const [formData, setFormData] = useState<any>({
    ...data,
    ...(actionType === DataGridActionType.Insert && { visible: true }),
  });
  const [capacityCategoryId, setCapacityCategoryId] = useState<number | null>(null);

  /**
   * Načte seznam capacityCategories.
   */
  const { data: capacityCategories, error: capacityCategoriesError } = useSWR<CapacityCategory[]>(
    `${API_URL.CAPACITY_CATEGORY_LIST}?parentId=${assetClass}`,
    fetcher,
    swrConfig,
  );

  const { data: task } = useSWR<Task>(`${API_URL.TASK_ITEM}?id=${taskId}`, fetcher, swrConfig);

  useEffect(() => {
    setFormData({
      ...data,
      ...(actionType === DataGridActionType.Insert && { visible: true }),
    });
  }, []);

  useEffect(() => {
    if (!capacityCategories || !task) return;
    setCapacityCategoryId(task.capacityCategoryId);
  }, [capacityCategories, task]);

  return (
    <Popup
      visible={isVisible}
      dragEnabled={false}
      closeOnOutsideClick
      onHiding={onClose}
      showTitle
      title={translate!('Add step', transFormName)}
      width={popupWidth}
      height={'auto'}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(formData, 'step');
        }}
      >
        <Form
          formData={formData}
          showColonAfterLabel={true}
          showValidationSummary={true}
          ref={formRef}
        >
          <GroupItem>
            <SimpleItem
              dataField="capacityCategoryId"
              editorType="dxSelectBox"
              editorOptions={{
                items: capacityCategories,
                valueExpr: 'id',
                displayExpr: 'name',
                showClearButton: true,
                value: capacityCategoryId,
                onValueChanged: (e: any) => {
                  setFormData({ ...formData, stepId: undefined });
                  setCapacityCategoryId(e.value);
                },
              }}
            >
              <Label text={translate!('Capacity category', transFormName)} />
            </SimpleItem>

            <SimpleItem
              dataField="stepId"
              editorType="dxSelectBox"
              editorOptions={{
                items: capacityCategoryId
                  ? stepList?.filter((x) => x.capacityId === capacityCategoryId)
                  : stepList,
                valueExpr: 'id',
                displayExpr: 'name',
              }}
            >
              <Label text={translate!('Step', transFormName)} />
              <RequiredRule message={translate!('Select step', transFormName)} />
            </SimpleItem>
          </GroupItem>

          <GroupItem colCount={4}>
            <GroupItem colSpan={2}></GroupItem>
            <ButtonItem>
              <ButtonOptions
                disabled={loading}
                type={'success'}
                text={translate!('Save', '')}
                useSubmitBehavior
              />
            </ButtonItem>
            <ButtonItem>
              <ButtonOptions type={'normal'} text={translate!('Close', '')} onClick={onClose} />
            </ButtonItem>
          </GroupItem>
        </Form>
      </form>
    </Popup>
  );
}
